import { ChangeDetectionStrategy, Component, DestroyRef, input, OnDestroy, output, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { AsyncPipe } from '@angular/common';
import { Observable, switchMap, tap } from 'rxjs';
import { BomAttachmentsState, BomAttachmentsStore } from './bom-attachments.store';
import { BomAttachmentDto } from './bom-attachment.dto';

export type AttachmentGroup = 'photos' | 'documents' | 'files';

export interface AttachmentsChange {
  bomItemId: number;
  hasAttachments: boolean;
  primaryPhotoAttachmentId: number | null;
}

// Extensions the picker offers — mirrors the API's allowed-extension map
export const ATTACHMENT_ACCEPT = '.pdf,.jpg,.jpeg,.png,.xlsx,.xls,.csv,.txt';

const DOCUMENT_CONTENT_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel'
];

// the tabs left to right; a newly selected item opens on the first that holds a file
const TAB_ORDER: AttachmentGroup[] = ['photos', 'documents', 'files'];

export function attachmentGroup(contentType: string): AttachmentGroup {
  if (contentType.startsWith('image/')) {
    return 'photos';
  }

  if (DOCUMENT_CONTENT_TYPES.includes(contentType)) {
    return 'documents';
  }

  return 'files';
}

@Component({
  selector: 'bom-attachments',
  templateUrl: './bom-attachments.component.html',
  styleUrl: './bom-attachments.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe]
})
// The card follows the bomItemId input: every change re-inits the store (switchMap drops stale in-flight responses)
export class BomAttachmentsComponent implements OnDestroy {
  readonly bomItemId = input.required<number | undefined>();

  // emits the id the mutation applied to (captured at click — a mid-flight selection change must not misroute the tree indicator)
  readonly attachmentsChanged = output<AttachmentsChange>();

  readonly state$: Observable<BomAttachmentsState>;

  readonly selectedTab = signal<AttachmentGroup>('photos');

  constructor(
    private store: BomAttachmentsStore,
    private destroyRef: DestroyRef
  ) {
    this.state$ = this.store.state$;

    toObservable(this.bomItemId)
      .pipe(
        // a new item opens on its first non-empty tab — an Inventor import's .xlsx sits under DOKUMENTY (0116);
        // re-loads after an upload or delete go through the store alone and keep the user's tab
        switchMap(bomItemId => this.store.init(bomItemId).pipe(tap(() => this.selectedTab.set(this.firstNonEmptyTab())))),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.store.destroy();
  }

  changeSelectedTab(tab: AttachmentGroup): void {
    this.selectedTab.set(tab);
  }

  // Called from the page's hidden file input — the upload icon lives in the card header
  onFilePicked(event: Event): void {
    const fileInput = event.target as HTMLInputElement;
    const file = fileInput.files?.[0];
    fileInput.value = ''; // re-picking the same file must fire (change) again

    const bomItemId = this.bomItemId();

    if (!file || bomItemId === undefined) {
      return;
    }

    // an upload never stars the file, so the main photo stays what it was before the call
    const primaryPhotoAttachmentId = this.primaryPhotoId();

    this.store.upload(bomItemId, file).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      // the store swallows failures into uploadError (the saveError precedent) — only a real upload flags the item
      if (!this.store.state.uploadError) {
        this.attachmentsChanged.emit({ bomItemId, hasAttachments: true, primaryPhotoAttachmentId });
      }
    });
  }

  onDeleteClick(attachment: BomAttachmentDto): void {
    const bomItemId = this.bomItemId();

    if (bomItemId === undefined) {
      return;
    }

    // counted before the call — after it the store may already hold another item's list
    const remaining = (this.store.state.attachments ?? []).filter(x => x.id !== attachment.id).length;
    const primaryPhotoAttachmentId = attachment.isPrimaryPhoto ? null : this.primaryPhotoId();

    this.store.softDelete(bomItemId, attachment.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.attachmentsChanged.emit({ bomItemId, hasAttachments: remaining > 0, primaryPhotoAttachmentId });
    });
  }

  onSetPrimaryClick(attachment: BomAttachmentDto): void {
    const bomItemId = this.bomItemId();

    if (bomItemId === undefined) {
      return;
    }

    this.store.setPrimaryPhoto(bomItemId, attachment.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.attachmentsChanged.emit({ bomItemId, hasAttachments: true, primaryPhotoAttachmentId: attachment.id });
    });
  }

  contentUrl(attachmentId: number): string {
    return this.store.contentUrl(attachmentId);
  }

  private firstNonEmptyTab(): AttachmentGroup {
    const state = this.store.state;
    return TAB_ORDER.find(group => this.groupOf(state, group).length > 0) ?? 'photos';
  }

  private primaryPhotoId(): number | null {
    return this.store.state.attachments?.find(x => x.isPrimaryPhoto)?.id ?? null;
  }

  groupOf(state: BomAttachmentsState, group: AttachmentGroup): BomAttachmentDto[] {
    return (state.attachments ?? []).filter(x => attachmentGroup(x.contentType) === group);
  }

  isImage(attachment: BomAttachmentDto): boolean {
    return attachmentGroup(attachment.contentType) === 'photos';
  }
}
